import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TSetListAction } from 'types/types';
import {
  DEFAULT_ORDER_BY,
  DEFAULT_PAGE,
  DEFAULT_PER_PAGE_ALL,
  ORDER_DIRECTION_DESC,
  TActionParams,
  TOptionsQuery,
} from 'types/common';
import { setShowToast } from 'store/toast/toastSlice';
import { ETypeToast } from 'app/enum';
import { TEmployeeManagement } from 'types/clientManagement/clientManagement';
import { t } from 'i18next';
import { translations } from '../../locales/translations';
import { getLabelError } from '../../helpers/funcs';
import { TSurveyProgress } from '../../types/surveyProgress/surveyProgress';
import { surveyProgressApi } from '../../api/surveyProgressApi';
import { TSurvey } from '../../types/survey/survey';

export type SurveyProgressState = {
  loading: boolean;
  listSurveyProgress: TSurveyProgress[];
  listSurveyByEmployee: TSurvey[];
  total: number;
  page: number;
  totalPage: number;
};

const initialState: SurveyProgressState = {
  loading: false,
  listSurveyProgress: [],
  listSurveyByEmployee: [],
  total: 0,
  page: 1,
  totalPage: 1,
};

export const getListSurveyProgress = createAsyncThunk(
  'surveyProgress/getListSurveyProgress',
  async (params: TOptionsQuery<TSurveyProgress>, thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    const { loadMore, ...rest } = params;
    if (rest.facilityId === '') {
      thunkAPI.dispatch(
        setListSurveyProgress({
          type: 'getList',
          surveyProgresses: [],
          total: 0,
          page: 1,
        }),
      );
    } else {
      const newParams: TOptionsQuery<TSurveyProgress> = {
        ...rest,
        orderBy: rest.orderBy ?? DEFAULT_ORDER_BY,
        orderDirection: rest.orderDirection ?? ORDER_DIRECTION_DESC,
        page: rest.page ?? DEFAULT_PAGE,
        perPage: rest.perPage ?? DEFAULT_PER_PAGE_ALL,
      };
      await surveyProgressApi
        .getListSurveyProgress(newParams)
        .then((response) => {
          thunkAPI.dispatch(
            setListSurveyProgress({
              type: loadMore ? 'loadMore' : 'getList',
              surveyProgresses: response.data.data,
              total: response.data._metadata.pagination.total,
              page: response.data._metadata.pagination.page,
            }),
          );
        })
        .catch((response) => {
          thunkAPI.dispatch(
            setShowToast({
              label: getLabelError(response.response.data.statusCode),
              type: ETypeToast.Error,
            }),
          );
          thunkAPI.dispatch(setLoading(false));
        });
    }

    thunkAPI.dispatch(setLoading(false));
  },
);

export const deleteSurveyProgress = createAsyncThunk(
  'surveyProgress/deleteSurveyProgress',
  async (surveyProgress: TSurveyProgress, thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    await surveyProgressApi
      .deleteSurveyProgress(String(surveyProgress?._id), surveyProgress?.surveyId ?? '')
      .then(() => {
        thunkAPI.dispatch(
          setListSurveyProgress({ type: 'delete', surveyProgress: surveyProgress }),
        );
        thunkAPI.dispatch(
          setShowToast({
            label: t(translations.ID_MASTER.DELETE_SUCCESS),
            type: ETypeToast.Success,
          }),
        );
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response.response.data.statusCode),
            type: ETypeToast.Error,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const updateSurveyProgress = createAsyncThunk(
  'surveyProgress/updateSurveyProgress',
  async (params: { id: string; data: TActionParams<TSurveyProgress> }, thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    await surveyProgressApi
      .updateSurveyProgress({ id: params.id, data: params.data.data })
      .then((response: any) => {
        thunkAPI.dispatch(
          setListSurveyProgress({
            type: 'edit',
            surveyProgress: { ...params.data.data, _id: response.data.data._id },
          }),
        );
        thunkAPI.dispatch(
          setShowToast({
            label: t(translations.ID_MASTER.UPDATE_SUCCESS),
            type: ETypeToast.Success,
          }),
        );
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response.response.data.statusCode),
            type: ETypeToast.Error,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const getListSurveyByEmployee = createAsyncThunk(
  'surveyProgress/getListSurveyByEmployee',
  async (id: string) => {
    const response = await surveyProgressApi.getListSurveyByEmployee(id);
    return response.data.data;
  },
);

export const addListEmployeeJoinSurvey = createAsyncThunk(
  'surveyProgress/addListEmployeeJoinSurvey',
  async (data: any[], thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    await surveyProgressApi
      .addListEmployeeJoinSurvey(data)
      .then(() => {
        thunkAPI.dispatch(
          getListSurveyProgress({
            surveyId: data[0].surveyId ?? '',
          }),
        );
        thunkAPI.dispatch(
          setShowToast({
            label: t(translations.ID_MASTER.REGISTER_SUCCESS),
            type: ETypeToast.Success,
          }),
        );
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response.response.data.statusCode),
            type: ETypeToast.Error,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

const slice = createSlice({
  name: 'surveyProgress',
  initialState,
  reducers: {
    setListSurveyProgress: (
      state,
      action: PayloadAction<{
        type: TSetListAction;
        surveyProgress?: TSurveyProgress;
        surveyProgresses?: TSurveyProgress[];
        page?: number;
        total?: number;
      }>,
    ) => {
      const { type, surveyProgress, surveyProgresses, total, page } = action.payload;
      if (surveyProgress) {
        switch (type) {
          case 'edit':
            state.listSurveyProgress = state.listSurveyProgress.map((m: TSurveyProgress) =>
              m._id === surveyProgress._id ? { ...surveyProgress, isActive: true } : m,
            );
            break;
          case 'delete':
            state.listSurveyProgress = state.listSurveyProgress.filter(
              (m: TSurveyProgress) => m._id !== surveyProgress._id,
            );
            break;
        }
      }
      if (surveyProgresses) {
        switch (type) {
          case 'getList':
            state.listSurveyProgress = surveyProgresses;
            break;
          case 'loadMore':
            state.listSurveyProgress = [...state.listSurveyProgress, ...surveyProgresses];
            break;
        }

        state.page = page ?? DEFAULT_PAGE;
        state.total = total ?? 0;
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getListSurveyByEmployee.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getListSurveyByEmployee.fulfilled,
      (state, action: PayloadAction<TSurvey[]>) => {
        state.loading = false;
        state.listSurveyByEmployee = action.payload;
      },
    );
    builder.addCase(addListEmployeeJoinSurvey.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addListEmployeeJoinSurvey.fulfilled, (state) => {
      state.loading = false;
    });
  },
});

export const { actions, reducer: SurveyProgressReducer } = slice;
export const { setListSurveyProgress, setLoading, setPage } = actions;
export default SurveyProgressReducer;
